import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext, SttList } from '../../../sporttia/all';
import ConfigurationRechargingOptionDialog from './ConfigurationRechargingOptionDialog';
import { useCanModifyConfiguration } from './useCanModifyConfiguration';

type ChargingOption = {
    id?: number;
    name: string;
    amount?: number;
    bonus?: number;
};

type ChargingOptionsResponse = {
    rows: ChargingOption[];
    count?: number;
};

export default function ConfigurationRecharges(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const canModify = useCanModifyConfiguration();
    const [chargingOptions, setChargingOptions] = useState<
        ChargingOptionsResponse | undefined
    >();
    const [selectedChargingOption, setSelectedChargingOption] =
        useState<ChargingOption | null>(null);

    /**
     * Load charging options
     */
    const loadChargingOptions = (): void => {
        cxt?.api('GET', `/scs/${cxt.sc?.id}/chargings/options`, {
            params: {
                page: 1,
                rows: 500,
            },
            success: (r: ChargingOptionsResponse) => {
                setChargingOptions(r);
            },
        });
    };

    /**
     * SC changes
     */
    useEffect(() => {
        if (cxt?.sc?.id) loadChargingOptions();
    }, [cxt?.sc?.id]);

    /**
     * Delete charging option
     */
    const delChargingOption = (chOpt: ChargingOption): void => {
        cxt?.api('DELETE', `/chargings/options/${chOpt.id}`, {
            confirmation: true,
            success: () => {
                loadChargingOptions();
                cxt.showMessage('S', t('Deleted'));
            },
        });
    };

    // -----| Render |-----
    return (
        <>
            <SttList
                title={t('Recharges')}
                onSelect={(e: ChargingOption) => setSelectedChargingOption(e)}
                onAdd={
                    canModify
                        ? () => setSelectedChargingOption({} as ChargingOption)
                        : undefined
                }
                onDelete={
                    canModify
                        ? (e: ChargingOption) => delChargingOption(e)
                        : undefined
                }
                data={
                    chargingOptions &&
                    chargingOptions.rows.map((opt: any) => ({
                        caption: opt.name,
                        objToSelect: opt,
                    }))
                }
            />

            <ConfigurationRechargingOptionDialog
                chargingOption={selectedChargingOption}
                onClose={() => {
                    loadChargingOptions();
                    setSelectedChargingOption(null);
                }}
            />
        </>
    );
}
